import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGetCarriers } from 'api';
import type { SelectOption } from 'components/fields/SelectSearch';
import type { APICarrierData } from 'types/api.types';

interface UseCarriersOptions {
  enabled?: boolean; // Whether to fetch carriers (default: true)
  staleTime?: number; // Cache time in milliseconds (default: 5 minutes)
  runner_type?: string; // Filter carriers by runner type (optional)
}

interface UseCarriersReturn {
  carrierOptions: SelectOption[];
  carriers: APICarrierData[];
  isLoadingCarriers: boolean;
  carriersError: any;
}

/**
 * Custom hook for fetching and managing carriers data
 * Fetches all carriers at once and caches them with React Query
 * Returns both raw carriers data and formatted SelectOption array
 */
export const useCarriers = (
  options: UseCarriersOptions = {},
): UseCarriersReturn => {
  const { enabled = true, staleTime = 5 * 60 * 1000, runner_type } = options;

  // Use React Query to fetch all carriers at once
  const {
    data: allCarriers = [],
    isLoading: isLoadingCarriers,
    error: carriersError,
  } = useQuery({
    queryKey: ['all-carriers', runner_type],
    queryFn: () => apiGetCarriers(1, 'all', runner_type),
    staleTime,
    enabled,
  });

  // Convert carriers to SelectOption format
  const carrierOptions = useMemo<SelectOption[]>(() => {
    return allCarriers.map((carrier: APICarrierData) => ({
      value: carrier.id,
      label: carrier.name,
    }));
  }, [allCarriers]);

  return {
    carrierOptions,
    carriers: allCarriers,
    isLoadingCarriers,
    carriersError,
  };
};
